import { useCrmLocale } from '../../ui/locale/crmLocale';
import React from 'react';
import type { SkeletonVariant } from './types';
import { SkeletonBlock } from './SkeletonBlock';
import { SkeletonCircle } from './SkeletonCircle';
import { SkeletonText } from './SkeletonText';
import { CardGridSkeleton } from './CardGridSkeleton';
import { TableSkeleton } from './TableSkeleton';
import { FormSkeleton } from './FormSkeleton';

interface PageSkeletonProps {
  variant?: SkeletonVariant;
  className?: string;
}

function renderBody(variant: SkeletonVariant): React.ReactNode {
  if (variant === 'public') {
    return (
      <>
        <div className="aa-skeleton-hero">
          <SkeletonText lines={3} />
          <SkeletonBlock width={180} height={40} radius={10} className="mt-4" />
        </div>
        <CardGridSkeleton cards={4} />
      </>
    );
  }

  if (variant === 'student') {
    return (
      <>
        <div className="aa-skeleton-section">
          <SkeletonBlock width={220} height={18} />
          <CardGridSkeleton cards={3} />
        </div>
        <div className="aa-skeleton-section">
          <SkeletonBlock width={180} height={18} />
          <TableSkeleton rows={5} columns={3} />
        </div>
      </>
    );
  }

  if (variant === 'sales' || variant === 'reception' || variant === 'manager' || variant === 'hr' || variant === 'accountant') {
    return (
      <>
        <div className="aa-skeleton-kpi-row">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="aa-skeleton-card">
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
        <TableSkeleton rows={7} columns={5} className="mt-4" />
      </>
    );
  }

  if (variant === 'employee') {
    return (
      <>
        <div className="aa-skeleton-card">
          <div className="flex items-center gap-4">
            <SkeletonCircle size={56} />
            <SkeletonText lines={2} />
          </div>
        </div>
        <FormSkeleton fields={5} className="mt-4" />
      </>
    );
  }

  if (variant === 'marketing') {
    return (
      <>
        <div className="aa-skeleton-card">
          <SkeletonText lines={2} />
        </div>
        <CardGridSkeleton cards={3} className="mt-4" />
      </>
    );
  }

  return (
    <>
      <div className="aa-skeleton-card">
        <SkeletonText lines={3} />
      </div>
      <TableSkeleton rows={6} columns={4} className="mt-4" />
    </>
  );
}

export const PageSkeleton: React.FC<PageSkeletonProps> = ({ variant = 'generic', className = '' }) => {
  const { t: crmText } = useCrmLocale();

  return (
    <section className={`aa-page-skeleton aa-page-skeleton--${variant} ${className}`} aria-label={crmText("Loading content")}>
      <div className="aa-skeleton-topbar">
        <div className="flex items-center gap-3">
          <SkeletonCircle size={32} />
          <SkeletonBlock width={180} height={16} />
        </div>
        <SkeletonBlock width={120} height={34} radius={8} />
      </div>
      {renderBody(variant)}
    </section>
  );
};
